import { useCallback } from 'react';

import { waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import { useReadContract } from 'wagmi';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { useWriteContractExtraGas } from '@utils/contract.utils';

import showToast from '@components/common/Toast';

import {
  AvailableChainIdType,
  CHAIN_ID,
} from '@constants/common/chain.constants';
import {
  ErrorMessages,
  SuccessMessages,
} from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';

import abi from '@abi/common/erc1155';

type Nft1155ApproveParamType = {
  spender: string;
  nftAddress: string;
  chainId: CHAIN_ID;
  handleUpdate?: () => Promise<void>;
};

export const useApprove = ({
  spender,
  nftAddress,
  chainId,
  handleUpdate,
}: Nft1155ApproveParamType) => {
  const { account } = useAuth();

  const {
    data: isApproved,
    refetch,
    isPending: isPendingIsApproved,
  } = useReadContract({
    abi,
    address: nftAddress as Address,
    functionName: 'isApprovedForAll',
    chainId,
    args: [account as Address, spender as Address],
  });

  const { writeContract, isPending: isPendingApprove } =
    useWriteContractExtraGas({
      config,
      mutation: {
        onSuccess: async (data) => {
          const transactionReceipt = await waitForTransactionReceipt(config, {
            hash: data,
          });
          await refetch();
          if (handleUpdate) {
            await handleUpdate();
          }
          showToast(
            {
              title: SuccessMessages.Approved,
              withLink: {
                hash: transactionReceipt.transactionHash,
                chainId,
              },
            },
            ToastType.Success,
          );
        },
        onError: (e) => {
          console.log('Approve:', e);
          showToast(
            { title: ErrorMessages.DeniedTransaction },
            ToastType.Error,
          );
        },
      },
    });

  const approve = useCallback(() => {
    writeContract({
      abi,
      address: nftAddress as Address,
      functionName: 'setApprovalForAll',
      args: [spender as Address, true],
      chainId,
    });
  }, [spender, nftAddress, writeContract, chainId]);

  return {
    approve,
    isApproved,
    refetch,
    isPendingIsApproved,
    isPendingApprove,
  };
};

type Nft1155BalanceParamType = {
  nftAddress: string;
  contractAddress?: string;
  chainId: AvailableChainIdType;
};

export const useBalance = ({
  nftAddress,
  chainId,
  contractAddress,
}: Nft1155BalanceParamType) => {
  const { account } = useAuth();

  return useReadContract({
    abi,
    address: nftAddress as Address,
    chainId,
    functionName: 'balanceOf',
    args: [(contractAddress as Address) || (account as Address), BigInt(0)],
    query: {
      enabled: !!nftAddress,
    },
  });
};

type Nft1155TotalSupplyParamType = {
  nftAddress: string;
  chainId: CHAIN_ID;
};

export const useTotalSupply = ({
  nftAddress,
  chainId,
}: Nft1155TotalSupplyParamType) => {
  return useReadContract({
    abi,
    address: nftAddress as Address,
    chainId,
    functionName: 'totalSupply',
    args: [],
    query: {
      enabled: !!nftAddress,
    },
  });
};
